import React, { useState } from 'react';
import { ShieldCheck, Clock, ShieldAlert } from 'lucide-react';
import { Badge } from '../ui';
import {
  IDENTITY_FAILED,
  IDENTITY_PENDING,
  IDENTITY_VERIFIED,
  getIdentityStatus,
  requiresIdentityVerification,
  createVerificationSession,
} from '../../utils/identityVerification';
import './Identity.css';

const START_ERROR = "Couldn't start verification — please try again.";

/**
 * Account-level identity badge for the owner's own dashboard. Paid tiers only.
 * Never rendered on any externally-shared view.
 */
const IdentityStatusBadge = ({ user }) => {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');

  if (!requiresIdentityVerification(user)) return null;

  const status = getIdentityStatus(user);

  if (status === IDENTITY_VERIFIED) {
    return (
      <span className="identity-badge-wrap">
        <Badge variant="success" className="identity-badge">
          <ShieldCheck size={13} aria-hidden="true" />
          Identity Verified
        </Badge>
      </span>
    );
  }

  if (status === IDENTITY_PENDING) {
    return (
      <span className="identity-badge-wrap">
        <Badge variant="neutral" className="identity-badge">
          <Clock size={13} aria-hidden="true" />
          Verification in progress
        </Badge>
      </span>
    );
  }

  const handleClick = async () => {
    setIsStarting(true);
    setError('');
    try {
      const verificationUrl = await createVerificationSession();
      if (!verificationUrl) {
        throw new Error('No verification URL returned');
      }
      // Full-page handoff to Stripe; leave the badge in its loading state.
      window.location.href = verificationUrl;
    } catch {
      setError(START_ERROR);
      setIsStarting(false);
    }
  };

  const label =
    status === IDENTITY_FAILED ? 'Retry identity check' : 'Verify your identity';

  return (
    <span className="identity-badge-wrap">
      <button
        type="button"
        className="identity-badge-button"
        onClick={handleClick}
        disabled={isStarting}
      >
        <Badge variant="warning" className="identity-badge">
          <ShieldAlert size={13} aria-hidden="true" />
          {isStarting ? 'Starting...' : label}
        </Badge>
      </button>
      {error && (
        <span className="identity-badge-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
};

export default IdentityStatusBadge;
